import capitalize from 'lodash/capitalize'
import { call, put } from 'redux-saga/effects';
import { get, post, update, del } from '../utils/net'
import { showToast } from './toastActions';
import { setLoading } from './uiActions';

const API_ACTION_TYPES = ['GET_ALL', 'GET', 'CREATE', 'DELETE', 'UPDATE', 'PATCH', 'EXPORT', 'IMPORT', 'DOWNLOAD', 'UPLOAD', 'GET_BY_ID']

const validStatuses = [200, 201, 206];

export function generateActionTypes(entity, actions) {
    const actionTypes = {};
    actions?.forEach(action => {
        actionTypes[`${entity}_${action}_REQUEST`] = `${entity}_${action}_REQUEST`;
        actionTypes[`${entity}_${action}_SUCCESS`] = `${entity}_${action}_SUCCESS`;
        actionTypes[`${entity}_${action}_FAILURE`] = `${entity}_${action}_FAILURE`;
    });
    actionTypes[`${entity}_RESET_REQUEST`] = `${entity}_RESET_REQUEST`;
    actionTypes[`${entity}_RESET_SUCCESS`] = `${entity}_RESET_SUCCESS`;

    return actionTypes;
}

/**
 * Generates the name of the loading state property for Redux.
 * @param {string} entityName - The name of the entity.
 * @param {string} action - The action being performed (e.g., 'GET', 'CREATE').
 * @returns {string} The name of the loading state property (e.g., 'isLoadingGetUser').
 */
export function getLoadingStateName(entityName, action) {
    const actionName = action?.split('_')?.map(capitalize)?.join('');
    return `isLoading${actionName}${capitalize(entityName)}`;
}

export function getActionTypes(entity, actions) {
    return generateActionTypes(entity.toUpperCase(), actions.map(action => action.toUpperCase()));
}

export function generateDefaultState(entityName, actions) {
    entityName = entityName.toLowerCase()
    return actions
        .map(action => {
            if (API_ACTION_TYPES.indexOf(action.toUpperCase()) < 0) {
                // eslint-disable-next-line no-console
              //  console.warn(`Action name: ${action} is invalid.`)
            }

            return {
                [getLoadingStateName(entityName, action)]: false
            }
        })
        .reduce(
            (result, action) => ({
                ...result,
                ...action
            }),
            {
                [entityName]: {},
                [`${entityName}List`]: []
            }
        )
}

export function* crudSaga(action, endpoint, successAction, failureAction, method) {

    try {

         // Start the loader
         yield put(setLoading(true));

        // Dispatch the request action
        yield put({ type: `${action.entity}_${action.action}_REQUEST` });

        let response;
        const cleanedPayload = cleanPayload(action.payload);
        //let message;
        if (method === 'POST') {
            response = yield call(post, endpoint, cleanedPayload);
        // message = "Record was saved successfully";
        } else if (method === 'PUT') {
            response = yield call(update, `${endpoint}`, cleanedPayload);
        // message = "Record was updated successfully";
        }else if (method === 'DELETE') {
            const { id } = cleanedPayload;
            response = yield call(del, `${endpoint}/${id}`);
            //message = "Record was deleted successfully";
        }
        // Check response status and handle success or error
        if (validStatuses.includes(response.status)) {
            // Operation was successful
            yield put(successAction(response.data));

            if(response?.data?.msgcode){
                yield put(showToast('error', 'Error', `${response?.data?.status} : ${response?.data?.msgcode} : ${response?.data?.message}`));
            }
        } else {
            // Operation failed - treat any non-200 response as an error
            yield put(failureAction(response));
            yield put(showToast('error', 'Error', `${response?.data?.status} : ${response?.data?.msgcode} : ${response?.data?.message}`));
        }
    } catch (error) {
      yield put(failureAction(error));
      yield put(showToast('error', 'Error', 'An error occurred'));
    }finally {
        // Dispatch the success/failure action and reset loading
        yield put({ type: `${action.entity}_${action.action}_SUCCESS` });
        // Stop the loader
        yield put(setLoading(false));
    }
  }

  export function* querySaga(action, endpoint, successAction, failureAction) {
    try {

           // Start the loader
        yield put(setLoading(true));

        const response = yield call(get, endpoint, { params: cleanPayload(action.payload) });
        yield put(successAction(response.data));
        if (validStatuses.includes(response.status)) {
            if(response?.data?.msgcode){
                yield put(showToast('error', 'Error', `${response?.data?.status} : ${response?.data?.msgcode} : ${response?.data?.message}`));
            }

            if(response?.data?.synced === false){
                yield put(showToast('error', 'Error', `${response?.status} : VS Store sync is fail. please try after sometime`));
            }

            if(response?.data?.synced === true){
                yield put(showToast('success', 'Success', `VS Store synchronization completed successfully.`));
            }
        }
    } catch (error) {
        // Handle any exceptions that occur during the saga
        yield put(failureAction(error));
        yield put(showToast('error', 'Error', 'An error occurred while fetching data'));
    } finally {
        // Stop the loader
        yield put(setLoading(false));
    }
}

export function* querySagaDownload(action, endpoint, successAction, failureAction, method = 'GET') {

    try {

         // Start the loader
         yield put(setLoading(true));

        // Make API call
        let response = null;
        const cleanedPayload = cleanPayload(action.payload);

        if (method === 'POST') {
            response = yield call(post, endpoint, cleanedPayload, { 
                responseType: 'blob' // Important to set this for file downloads
            });
        }else{
            response = yield call(get, endpoint, { 
                params: cleanedPayload,
                responseType: 'blob' // Important to set this for file downloads
            });
        }
        
        // Check response status for success
        if ( validStatuses.includes(response.status) ) {
            const contentDisposition = response.headers['content-disposition'];
            const isFileDownload = contentDisposition && contentDisposition.includes('attachment');

            if (isFileDownload) {
                // Extract the file name from content-disposition header
                const filename = contentDisposition
                    .split('filename=')[1]
                    .replace(/"/g, '');

                // Create a blob from the response data
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                
                // Create a download link
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = filename;

                // Append to the DOM and trigger click to download the file
                document.body.appendChild(link);
                link.click();

                // Clean up
                document.body.removeChild(link);
                yield put(successAction(response.data));
            } else {
                // Handle non-file responses
                yield put(successAction(response.data));
            }
        } else {
            // Show error if status code is not successful
            yield put(showToast('error', 'Error', `${response?.data?.status} : ${response?.data?.msgcode} : ${response?.data?.message}`));
        }
    } catch (error) {
        // Handle exceptions during the saga
        yield put(failureAction(error));
        yield put(showToast('error', 'Error', 'An error occurred while fetching data'));
    }
    finally {
        // Stop the loader
        yield put(setLoading(false));
    }
}

export const cleanPayload = (payload) => {
    return Object.entries(payload || {}).reduce((acc, [key, value]) => {
        if (value !== null && value !== '' && value !== undefined) {
            acc[key] = value;
        }
        return acc;
    }, {});
};
